import { Col, Row } from "react-grid-system";
import { theme } from "../../../theme";
import TitleSmall from "../../atoms/typography/TitleSmall";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { useState, useEffect } from "react";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import { useTranslation } from "react-i18next";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import { SortingArrowsIcon } from "../../atoms/icons/SortingArrowsIcon";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import styled from "styled-components";
import BodySmall from "../../atoms/typography/BodySmall";
import dayjs from "dayjs";
import { formatPrice, getCurrencySymbol } from "../../../utils/formatters";
import TextField from "../../atoms/inputs/TextField";
import MultiSelect from "../../atoms/inputs/MultiSelect";
import PaginationComponent from "../PaginationComponent";
import type { InvoiceProcessStatus } from "../../../models/InvoiceStatusModel";
import { getInvoiceListStatusBadge } from "../../../utils/invoicesHelpers";
import { useNavigate } from "react-router-dom";
import type { InvoiceSortBy } from "../../../models/InvoiceSortBy";
import type { SortDirection } from "../../../models/SortDirection";
import {
  InvoiceValidationRoute,
  InvoiceAssessmentRoute,
} from "../../../utils/routes";

export interface OpenInvoice {
  id: string;
  status: InvoiceProcessStatus;
  invoiceNumber: string;
  recipientName: string;
  invoiceDate: Date;
  dueDate: Date;
  amount: number;
  currency?: string;
}

const StatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: ${theme.colors.grey02};
`;

const InvoiceRow = styled(Row)`
  padding: 12px 8px;
  border-bottom: 1px solid ${theme.colors.grey05};
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.hover};
  }
`;

const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const statusOptions: Array<{ value: string; label: string }> = [
  {
    value: "VALIDATION",
    label: "invoices.underValidation",
  },
  {
    value: "ASSESSMENT",
    label: "invoices.underAssessment",
  },
];

interface Props {
  invoices: Array<OpenInvoice>;
  clientIdentifier: string;
  deliveryApprovals?: {
    numberOfApproved: number;
    totalNumber: number;
  };
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  selectedStatuses?: Array<string>;
  onStatusChange?: (statuses: Array<string> | undefined) => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  sortBy?: InvoiceSortBy;
  sortDirection?: SortDirection;
  onSortChange?: (sortBy: InvoiceSortBy, direction: SortDirection) => void;
}

const OpenInvoicesList: React.FC<Props> = ({
  invoices,
  clientIdentifier,
  deliveryApprovals,
  currentPage,
  totalPages,
  onPageChange,
  selectedStatuses,
  onStatusChange,
  searchTerm,
  onSearchChange,
  sortBy,
  sortDirection,
  onSortChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (invoices.length > 0) setOpen(true);
  }, [invoices.length]);

  const handleRowClick = (invoice: OpenInvoice) => {
    if (invoice.status === "VALIDATION") {
      navigate(`/invoices/validate/${invoice.id}/${clientIdentifier}`, {
        state: { from: InvoiceValidationRoute.id },
      });
    } else {
      navigate(`/invoices/assess/${clientIdentifier}/${invoice.id}`, {
        state: { from: InvoiceAssessmentRoute.id },
      });
    }
  };

  const handleSort = (column: InvoiceSortBy) => {
    if (!onSortChange) return;
    if (sortBy === column) {
      onSortChange(column, sortDirection === "ASC" ? "DESC" : "ASC");
    } else {
      onSortChange(column, "ASC");
    }
  };

  return (
    <div>
      <Row nogutter align="center" justify="between">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <TitleSmall color={theme.colors.primary}>
            {t("invoiceAssessment.openInvoices")} -
          </TitleSmall>
          {invoices.length === 0 ? (
            <BodySmall color={theme.colors.textSecondary}>
              <i>{t("invoiceAssessment.noOpenInvoices")}</i>
            </BodySmall>
          ) : // TODO: show total sum
          null}
        </div>
        <ChevronButton
          type={open ? "up" : "down"}
          onClick={() => setOpen(!open)}
        />
      </Row>

      <SlideOutHeight isVisible={open}>
        <HorizontalSpacer spacing={1} />
        <Row nogutter justify="between">
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <TextField
              placeholder={t("common.search")}
              value={searchTerm ?? ""}
              onChange={(value) => onSearchChange?.(value)}
            />
            <MultiSelect
              options={statusOptions}
              selectedOptions={selectedStatuses ?? []}
              onChange={(selectedOption) => onStatusChange?.(selectedOption)}
              onClickSelectAll={() => onStatusChange?.(undefined)}
              title={t("common.status")}
            />
          </div>
          {deliveryApprovals && (
            <StatContainer>
              <BodySmall fontweight={500}>
                {deliveryApprovals.numberOfApproved}{" "}
                {t("invoiceAssessment.of").toLowerCase()}{" "}
                {deliveryApprovals.totalNumber}
              </BodySmall>
              <BodySmall>
                {t("invoiceAssessment.deliveryApprovedInvoices").toLowerCase()}
              </BodySmall>
            </StatContainer>
          )}
        </Row>
        <HorizontalSpacer spacing={1} />
        <Row
          style={{
            padding: 8,
            borderBottom: `1px solid ${theme.colors.grey05}`,
          }}
          nogutter
          align="center"
        >
          <Col lg={2} style={{ display: "flex", alignItems: "center" }}>
            <TableHeaderText>{t("invoices.invoiceNumber")}</TableHeaderText>
          </Col>
          <Col lg={2} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TableHeaderText>{t("common.recipient")}</TableHeaderText>
          </Col>
          <Col lg={2}>
            <SortableHeader onClick={() => handleSort("INVOICE_DATE")}>
              <TableHeaderText>{t("invoices.invoiceDate")}</TableHeaderText>
              <SortingArrowsIcon
                color={
                  sortBy === "INVOICE_DATE" ? theme.colors.primary : undefined
                }
              />
            </SortableHeader>
          </Col>
          <Col lg={2}>
            <SortableHeader onClick={() => handleSort("DUE_DATE")}>
              <TableHeaderText>{t("invoices.dueDate")}</TableHeaderText>
              <SortingArrowsIcon
                color={sortBy === "DUE_DATE" ? theme.colors.primary : undefined}
              />
            </SortableHeader>
          </Col>
          <Col lg={1.8}>
            <SortableHeader
              onClick={() => handleSort("TOTAL_AMOUNT_INCLUDING_VAT")}
            >
              <TableHeaderText>{t("invoices.amount")}</TableHeaderText>
              <SortingArrowsIcon
                color={
                  sortBy === "TOTAL_AMOUNT_INCLUDING_VAT"
                    ? theme.colors.primary
                    : undefined
                }
              />
            </SortableHeader>
          </Col>
          <Col lg={2} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TableHeaderText>{t("common.status")}</TableHeaderText>
          </Col>
          <Col lg={0.2}></Col>
        </Row>
        {invoices.map((invoice) => (
          <InvoiceRow
            key={invoice.id}
            nogutter
            onClick={() => handleRowClick(invoice)}
          >
            <Col lg={2}>
              <BodySmall>{invoice.invoiceNumber}</BodySmall>
            </Col>
            <Col lg={2}>
              <BodySmall>{invoice.recipientName}</BodySmall>
            </Col>
            <Col lg={2}>
              <BodySmall>
                {dayjs(invoice.invoiceDate).format("YYYY-MM-DD")}
              </BodySmall>
            </Col>
            <Col lg={2}>
              <BodySmall>
                {dayjs(invoice.dueDate).format("YYYY-MM-DD")}
              </BodySmall>
            </Col>
            <Col lg={1.8}>
              <BodySmall>
                {formatPrice(invoice.amount)}{" "}
                {getCurrencySymbol(invoice.currency)}
              </BodySmall>
            </Col>
            <Col lg={2}>{getInvoiceListStatusBadge(invoice.status)}</Col>
            <Col lg={0.2}>
              <ChevronSmallRightIcon />
            </Col>
          </InvoiceRow>
        ))}
        <HorizontalSpacer spacing={1} />
        <Row nogutter justify="end">
          <PaginationComponent
            numberOfPages={totalPages}
            currentPageIndex={currentPage}
            setCurrentPageIndex={onPageChange}
          />
        </Row>
      </SlideOutHeight>
    </div>
  );
};

export default OpenInvoicesList;
